/**
 * TouchControls.tsx — Mobile / touch device on-screen controls
 *
 * BETA 16.57 — Wave 2 of the mega settings push. Per Dylan: "i cant play
 * this on my phone. no controls for touch screen put in."
 *
 * Layout:
 *   - Left half: floating virtual joystick. Touch anywhere on the left
 *     half to spawn the joystick at that point; drag to move. Lift to
 *     stop. Auto-aim is already handled by the game so no right-stick
 *     for aiming is needed.
 *   - Bottom-right: three big tap buttons — E (interact), Q (active
 *     item), and a small fullscreen toggle in the top-right.
 *   - Top-right (small): pause button.
 *
 * Strategy: this overlay translates touch events into the same
 * `state.keys` set that keyboard input writes to (w/a/s/d for direction,
 * e for interact, q for active item, escape for pause). The engine
 * doesn't need to know it's a touch — the input layer is the only place
 * that has to be touch-aware.
 *
 * Detection: shown when either `'ontouchstart' in window` OR the user
 * forces touch controls on via Settings (touchControlsForced). On a
 * desktop with a mouse the controls stay hidden.
 */
import { useEffect, useRef, useState } from 'react';

export interface TouchControlsProps {
  /** Force-show even on non-touch devices (Settings toggle hook). */
  forceShow?: boolean;
  /** Add/remove a key on the engine's `state.keys` Set. The host
   *  component owns the mapping. */
  onKey: (key: string, down: boolean) => void;
  /** Pause hotkey — fires Escape press on the host. */
  onPause: () => void;
}

// BETA 16.70 — These helpers moved to game/deviceDetect.ts so non-React
// modules (engine, livePlayTracker) can use them without dragging React
// imports into core game logic. Re-exported here so existing imports
// (`from './TouchControls'`) keep working.
export { isTouchDevice, isIPhoneSafari, isStandalonePWA } from '../game/deviceDetect';
import { isTouchDevice, isIPhoneSafari, isStandalonePWA, isHandheldGamingDevice } from '../game/deviceDetect';

const JOY_RADIUS = 60;       // visual radius of the joystick base
const JOY_KNOB_RADIUS = 32;  // knob (thumb) radius
const JOY_DEADZONE = 0.18;   // 0..1 — below this, no movement output
const ACTION_BTN_SIZE = 80;  // E (Interact) tap button. Q removed in BETA 19.19 — Q-tray was deprecated in 16.99.

interface JoystickState {
  active: boolean;
  cx: number;          // joystick base center, screen px
  cy: number;
  knobX: number;       // knob center, screen px
  knobY: number;
  touchId: number | null;
}

const initialJoy: JoystickState = {
  active: false, cx: 0, cy: 0, knobX: 0, knobY: 0, touchId: null,
};

export default function TouchControls({ forceShow, onKey, onPause }: TouchControlsProps) {
  const [show, setShow] = useState(false);
  const [, forceRerender] = useState(0); // for joystick visual updates
  // BETA 16.66 — portrait + a2hs overlays moved to MobileGuards (Home
  // top-level). TouchControls now only renders the joystick + buttons.
  const joyRef = useRef<JoystickState>({ ...initialJoy });
  // Track which dpad-cardinals are currently "pressed" so we can release
  // them when the joystick angle changes.
  const heldRef = useRef<Set<string>>(new Set());
  // Refs to action button DOM nodes — touchstart on these consumes the
  // touch (so it doesn't also spawn the joystick if it landed on them).
  // BETA 19.19 — qRef removed alongside the dead Q button (Q-tray went
  // away in 16.99, but the touch UI's Q tap button was still being
  // rendered and just sending 'q' keydowns into the void). Same root
  // cause as BUG #3 (SettingsScreen stale "Active Item (Q)" label fixed
  // in 19.13).
  const eRef = useRef<HTMLDivElement>(null);
  const pauseRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // BETA 16.74 — Per Dylan (ROG Ally X): "and on the rog ally version
    // its acting like i am on cell phon cause i see the Q and E circle
    // buttons in game on screen. also what is this orange square in the
    // top left corner of the screen with 4 lines verticle in it on my
    // screen?" That orange square is the touch pause button I added in
    // BETA 16.65 — it shows because ROG Ally has a touch screen so
    // isTouchDevice() returns true. But ROG Ally / Steam Deck / Switch
    // are CONTROLLER-FIRST devices — the player wants gamepad as primary
    // input even though touch is available. Skip the on-screen virtual
    // joystick + Q/E/pause buttons on handheld gaming devices. Touch
    // still works (the canvas itself receives clicks for menu hover etc),
    // but the cluttering overlay is suppressed in favor of the gamepad
    // experience.
    if (isHandheldGamingDevice()) {
      setShow(!!forceShow);
      return;
    }
    setShow(!!forceShow || isTouchDevice());
  }, [forceShow]);

  // Translate a normalized joystick vector to held cardinal keys.
  // We map to the same w/a/s/d scheme the engine reads. Diagonals fire
  // two keys at once (engine normalizes diagonal speed).
  const updateMovement = (nx: number, ny: number) => {
    const newHeld = new Set<string>();
    const mag = Math.hypot(nx, ny);
    if (mag >= JOY_DEADZONE) {
      // ny is screen-space (down positive); engine maps 'w' to dy -= 1
      // so we send 'w' for upward motion (ny < 0) and 's' for ny > 0.
      if (nx < -0.35) newHeld.add('a');
      if (nx > 0.35) newHeld.add('d');
      if (ny < -0.35) newHeld.add('w');
      if (ny > 0.35) newHeld.add('s');
    }
    // Press newly added keys
    newHeld.forEach((k) => {
      if (!heldRef.current.has(k)) onKey(k, true);
    });
    // Release keys no longer held
    heldRef.current.forEach((k) => {
      if (!newHeld.has(k)) onKey(k, false);
    });
    heldRef.current = newHeld;
  };

  // Joystick touch handlers — mounted on the LEFT-HALF backdrop.
  const onJoyTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!show) return;
    // If the touch landed on an action button, ignore it here (the button
    // handler will fire). Use the target for cheap test.
    const tgt = e.target as Element | null;
    if (tgt && (tgt.closest('[data-touch-action]'))) return;
    const t = e.changedTouches[0];
    joyRef.current = {
      active: true,
      cx: t.clientX,
      cy: t.clientY,
      knobX: t.clientX,
      knobY: t.clientY,
      touchId: t.identifier,
    };
    forceRerender(v => v + 1);
  };

  const onJoyTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!show || !joyRef.current.active) return;
    let t: React.Touch | undefined;
    for (let i = 0; i < e.changedTouches.length; i++) {
      const c = e.changedTouches[i];
      if (c.identifier === joyRef.current.touchId) { t = c; break; }
    }
    if (!t) return;
    e.preventDefault();
    const dx = t.clientX - joyRef.current.cx;
    const dy = t.clientY - joyRef.current.cy;
    const mag = Math.hypot(dx, dy);
    let kx = t.clientX, ky = t.clientY;
    if (mag > JOY_RADIUS) {
      kx = joyRef.current.cx + (dx / mag) * JOY_RADIUS;
      ky = joyRef.current.cy + (dy / mag) * JOY_RADIUS;
    }
    joyRef.current.knobX = kx;
    joyRef.current.knobY = ky;
    forceRerender(v => v + 1);
    // Normalize to -1..1 and feed movement.
    const nx = (kx - joyRef.current.cx) / JOY_RADIUS;
    const ny = (ky - joyRef.current.cy) / JOY_RADIUS;
    updateMovement(nx, ny);
  };

  const onJoyTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!show) return;
    let ended = false;
    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === joyRef.current.touchId) { ended = true; break; }
    }
    if (!ended) return;
    joyRef.current = { ...initialJoy };
    forceRerender(v => v + 1);
    // Release all held movement keys.
    heldRef.current.forEach((k) => onKey(k, false));
    heldRef.current.clear();
  };

  // Action buttons fire press-on-touch then release-on-end.
  // BETA 19.19 — Was `'e' | 'q'`; Q removed (see qRef comment above).
  const makeActionHandlers = (key: 'e') => ({
    onTouchStart: (e: React.TouchEvent) => {
      e.preventDefault();
      onKey(key, true);
    },
    onTouchEnd: (e: React.TouchEvent) => {
      e.preventDefault();
      onKey(key, false);
    },
    onTouchCancel: () => onKey(key, false),
  });

  if (!show) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 150,
        pointerEvents: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
      }}
    >
      {/* Left half — joystick capture region */}
      <div
        onTouchStart={onJoyTouchStart}
        onTouchMove={onJoyTouchMove}
        onTouchEnd={onJoyTouchEnd}
        onTouchCancel={onJoyTouchEnd}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '50vw',
          height: '100vh',
          pointerEvents: 'auto',
          background: 'transparent',
        }}
      />

      {/* Joystick visualization (only when active) */}
      {joyRef.current.active && (
        <>
          <div
            style={{
              position: 'absolute',
              left: joyRef.current.cx - JOY_RADIUS,
              top: joyRef.current.cy - JOY_RADIUS,
              width: JOY_RADIUS * 2,
              height: JOY_RADIUS * 2,
              borderRadius: '50%',
              background: 'rgba(20,20,20,0.45)',
              border: '2px solid rgba(255,255,255,0.6)',
              pointerEvents: 'none',
              boxShadow: '0 0 16px rgba(0,0,0,0.5)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: joyRef.current.knobX - JOY_KNOB_RADIUS,
              top: joyRef.current.knobY - JOY_KNOB_RADIUS,
              width: JOY_KNOB_RADIUS * 2,
              height: JOY_KNOB_RADIUS * 2,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.85)',
              border: '2px solid rgba(255,255,255,0.95)',
              pointerEvents: 'none',
              boxShadow: '0 0 12px rgba(255,255,255,0.45)',
            }}
          />
        </>
      )}

      {/* Bottom-right action button — single Interact (E) button only.
          BETA 19.19 — Q tap button removed. The Q-tray feature was
          deprecated in BETA 16.99; the touch UI was still rendering a
          large blue Q button at the bottom-right that just dispatched
          'q' keydowns into the void. Removing it frees ~94px of vertical
          space and stops misleading mobile players about controls. */}
      <div
        style={{
          position: 'absolute',
          right: 24,
          bottom: 36,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          pointerEvents: 'none',
        }}
      >
        <div
          ref={eRef}
          data-touch-action="e"
          {...makeActionHandlers('e')}
          style={{
            width: ACTION_BTN_SIZE + 14,
            height: ACTION_BTN_SIZE + 14,
            borderRadius: '50%',
            background: 'rgba(20,60,30,0.6)',
            border: '3px solid rgba(120,255,160,0.9)',
            color: '#fff',
            fontSize: 28,
            fontWeight: 700,
            fontFamily: "'Orbitron', monospace",
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'auto',
            boxShadow: '0 0 18px rgba(0,0,0,0.55)',
            textShadow: '0 0 6px rgba(120,255,160,0.6)',
            touchAction: 'none',
          }}
        >E</div>
      </div>

      {/* TOP-LEFT big pause button.
          BETA 16.65 — Per Dylan (iPhone Safari): "i dont knw how to pause."
          Was 44×44 top-right at right:90 — competing with FPS readout +
          fullscreen button + bot toggle + iOS URL bar = effectively
          invisible. Now: top-left, 68×68, bright orange ring, bigger
          glyph, with safe-area-inset-top so the iPhone notch / Dynamic
          Island doesn't eat it. */}
      <div
        ref={pauseRef}
        data-touch-action="pause"
        onTouchStart={(e) => { e.preventDefault(); onPause(); }}
        style={{
          position: 'absolute',
          top: 'max(12px, calc(env(safe-area-inset-top) + 8px))',
          left: 'max(12px, calc(env(safe-area-inset-left) + 8px))',
          width: 68,
          height: 68,
          borderRadius: 12,
          background: 'rgba(0,0,0,0.85)',
          border: '3px solid rgba(255,170,60,0.95)',
          color: '#ffaa3c',
          fontSize: 34,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'auto',
          touchAction: 'none',
          boxShadow: '0 0 14px rgba(255,170,60,0.45)',
          letterSpacing: -2, // pull the ‖ glyphs together
        }}
      >‖‖</div>
      {/* BETA 16.66 — Portrait + Add-to-Home-Screen overlays moved to
          MobileGuards (rendered at Home root) so they cover the entire
          app, not just GameCanvas. Removed from here to avoid duplication. */}
    </div>
  );
}
